import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useListUsersQuery,
  type AdminUserListItem,
} from '../../redux/users/users.api';

const DEBOUNCE_MS = 300;
const PAGE_SIZE = 20;

const ADMIN_ROLE = 3;
const TESTER_ROLE = 2;

const roleLabel = (role: number): string => {
  if (role === ADMIN_ROLE) return 'Admin';
  if (role === TESTER_ROLE) return 'Tester';
  return 'User';
};

const roleClass = (role: number): string => {
  if (role === ADMIN_ROLE) return 'admin-users__role admin-users__role--admin';
  if (role === TESTER_ROLE) return 'admin-users__role admin-users__role--tester';
  return 'admin-users__role';
};

const formatLocation = (loc: AdminUserListItem['location']): string => {
  if (!loc) return '—';
  const parts = [loc.city, loc.country].filter(Boolean);
  return parts.length ? parts.join(', ') : '—';
};

const initial = (u: AdminUserListItem): string => {
  const source = u.firstName || u.email || '?';
  return source.charAt(0).toUpperCase();
};

export const AdminUsers = (): React.JSX.Element => {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setPage(1);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isFetching, error } = useListUsersQuery({
    search: debouncedSearch || undefined,
    page,
    limit: PAGE_SIZE,
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const hasItems = data && data.items.length > 0;
  const empty = data && data.items.length === 0;

  return (
    <div className="admin-users">
      <div className="admin-users__header">
        <div className="admin-users__title">
          <h2>Users</h2>
          {data && (
            <span className="admin-users__count">
              {data.total.toLocaleString()} total
            </span>
          )}
        </div>
        <input
          className="admin-users__search"
          type="search"
          placeholder="Search by name, email, or username…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
      </div>

      {error && (
        <div className="admin-users__error">
          Failed to load users. Try again.
        </div>
      )}

      {isLoading && !data && (
        <div className="admin-users__loading">Loading users…</div>
      )}

      {empty && (
        <div className="admin-tab-empty">
          <h2 className="admin-tab-empty__title">No users</h2>
          <p className="admin-tab-empty__sub">
            {debouncedSearch
              ? `No users match "${debouncedSearch}".`
              : 'No users yet.'}
          </p>
        </div>
      )}

      {hasItems && (
        <>
          <div className="admin-users__table-wrap">
            <table className="admin-users__table">
              <thead>
                <tr>
                  <th className="admin-users__avatar-cell"></th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Location</th>
                  <th>Role</th>
                  <th>Verified</th>
                </tr>
              </thead>
              <tbody>
                {data!.items.map((u) => (
                  <tr
                    key={u.id}
                    className="admin-users__row admin-users__row--clickable"
                    onClick={() => navigate(`/admin/dashboard/users/${u.id}`)}
                  >
                    <td className="admin-users__avatar-cell">
                      {u.avatarUrl ? (
                        <img
                          src={u.avatarUrl}
                          alt=""
                          className="admin-users__avatar"
                        />
                      ) : (
                        <div className="admin-users__avatar admin-users__avatar--blank">
                          {initial(u)}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="admin-users__name">
                        <span>
                          {[u.firstName, u.lastName].filter(Boolean).join(' ') ||
                            '—'}
                        </span>
                        {u.username && (
                          <span className="admin-users__username">
                            @{u.username}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>{u.email}</td>
                    <td>{formatLocation(u.location)}</td>
                    <td>
                      <span className={roleClass(u.role)}>
                        {roleLabel(u.role)}
                      </span>
                    </td>
                    <td>
                      <span
                        className={
                          u.verified
                            ? 'admin-users__verified admin-users__verified--yes'
                            : 'admin-users__verified admin-users__verified--no'
                        }
                        aria-label={u.verified ? 'Verified' : 'Not verified'}
                      >
                        {u.verified ? '●' : '○'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {isFetching && !isLoading && (
              <div className="admin-users__fetching-overlay">Updating…</div>
            )}
          </div>

          <div className="admin-users__pagination">
            <button
              type="button"
              className="admin-users__page-btn"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || isFetching}
            >
              ← Previous
            </button>
            <span className="admin-users__page-info">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              className="admin-users__page-btn"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || isFetching}
            >
              Next →
            </button>
          </div>
        </>
      )}
    </div>
  );
};
